import React, { ChangeEvent, useState, useMemo } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Typography, { TypographyProps } from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { NumericFormat, NumericFormatProps } from "react-number-format";

export const useNumberFormatter = (decimalPlaces = 4, isCurrency = false) => {
  return React.useCallback(
    (num: number): string => {
      if (num === 0 || isNaN(num)) {
        if (isCurrency) return "$0." + "0".repeat(decimalPlaces);
        return "0." + "0".repeat(decimalPlaces);
      }
      const formatter = new Intl.NumberFormat("en-US", {
        style: isCurrency ? "currency" : "decimal",
        currency: isCurrency ? "USD" : undefined,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
      return formatter.format(num);
    },
    [decimalPlaces, isCurrency]
  );
};

interface BaseNumericProps {
  value: number;
  decimalPlaces: number;
  isCurrency: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

export interface NumericEntryFieldProps extends BaseNumericProps {
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  allowNegative?: boolean;
  allowLeadingZeros?: boolean;
  thousandSeparator?: string | boolean;
  decimalSeparator?: string;
  textFieldProps?: Omit<
    TextFieldProps,
    "value" | "onChange" | "type" | "slotProps.input"
  >;
}

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  allowNegative?: boolean;
  allowLeadingZeros?: boolean;
  thousandSeparator?: string | boolean;
  decimalSeparator?: string;
  maxDecimalPlaces?: number;
}

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
  function NumericFormatCustom(props, ref) {
    const {
      onChange,
      allowNegative = false,
      allowLeadingZeros = false,
      thousandSeparator = ",",
      decimalSeparator = ".",
      maxDecimalPlaces = 4,
      ...other
    } = props;

    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) => {
          onChange({
            target: {
              name: props.name,
              value: values.value,
            },
          });
        }}
        thousandSeparator={thousandSeparator}
        decimalSeparator={decimalSeparator}
        allowNegative={allowNegative}
        allowLeadingZeros={allowLeadingZeros}
        decimalScale={maxDecimalPlaces}
        fixedDecimalScale={false}
        valueIsNumericString
        isAllowed={(values) => {
          const { formattedValue, floatValue } = values;

          if (!formattedValue) return true;

          const decimalIndex = formattedValue.indexOf(decimalSeparator);
          if (decimalIndex !== -1) {
            const decimalPlaces = formattedValue.length - decimalIndex - 1;
            if (decimalPlaces > maxDecimalPlaces) {
              return false;
            }
          }
          return true;
        }}
      />
    );
  }
);

export const NumericEntryField: React.FC<NumericEntryFieldProps> = ({
  value,
  onChange,
  min = -9999999999.9999,
  max = 9999999999.9999,
  decimalPlaces = 4,
  isCurrency = false,
  startAdornment,
  endAdornment,
  label,
  error,
  helperText,
  required,
  disabled,
  allowNegative = false,
  allowLeadingZeros = false,
  thousandSeparator = ",",
  decimalSeparator = ".",
  textFieldProps = {},
}) => {
  const [valClampText, setValClampText] = useState<string | undefined>(
    undefined
  );
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseFloat(event.target.value) || 0;

    let clampedValue = Math.min(max, Math.max(min, numericValue));
    if (clampedValue !== numericValue) {
      setValClampText(`Value cannot be lower than ${min} or exceed ${max}.`);
      setTimeout(() => {
        setValClampText(undefined);
      }, 10000);
    }

    const finalValue = Number(clampedValue.toFixed(decimalPlaces));
    onChange(finalValue);
  };
  return (
    <TextField
      {...textFieldProps}
      label={label}
      value={value || "0." + "0".repeat(decimalPlaces)}
      onChange={handleChange}
      error={error}
      helperText={valClampText || helperText}
      required={required}
      disabled={disabled}
      name="numberformat"
      slotProps={{
        input: {
          inputComponent: NumericFormatCustom as any,
          startAdornment: isCurrency ? "$" : startAdornment,
          endAdornment,
        },
        htmlInput: {
          allowNegative,
          allowLeadingZeros,
          thousandSeparator,
          decimalSeparator,
          maxDecimalPlaces: decimalPlaces,
        },
      }}
      onWheel={() => {}}
    />
  );
};

interface NumericDisplayFieldProps extends BaseNumericProps {
  label?: string;
  variant?: "inline" | "field";
  typographyProps?: Omit<TypographyProps, "children">;
  containerProps?: React.ComponentProps<typeof Box>;
}

export const NumericDisplayField: React.FC<NumericDisplayFieldProps> = ({
  value,
  decimalPlaces = 2,
  isCurrency = false,
  startAdornment,
  endAdornment,
  label,
  variant = "inline",
  typographyProps = {},
  containerProps = {},
}) => {
  const formatNumber = useNumberFormatter(decimalPlaces, isCurrency);
  const formattedValue = formatNumber(value);

  if (variant === "inline") {
    return (
      <Typography {...typographyProps}>
        {startAdornment}
        {formattedValue}
        {endAdornment}
      </Typography>
    );
  }
  return (
    <Box {...containerProps}>
      {label && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 0.5,
            color: "text.secondary",
            fontSize: "0.75rem",
          }}
        >
          {label}
        </Typography>
      )}
      <Typography {...typographyProps}>
        {startAdornment}
        {formattedValue}
        {endAdornment}
      </Typography>
    </Box>
  );
};

interface PercentFieldProps
  extends Omit<TextFieldProps, "value" | "onChange" | "type"> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  decimalPlaces?: number;
}

export const PercentageField: React.FC<PercentFieldProps> = ({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 0.01,
  decimalPlaces = 2,
  label = "Percentage",
  helperText,
  ...textFieldProps
}) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const displayVal = parseFloat(event.target.value);

    if (isNaN(displayVal) || event.target.value === "") {
      onChange(0);
    } else {
      const clampedVal = Math.max(min, Math.min(max, displayVal));
      onChange(parseFloat((clampedVal / 100).toFixed(4)));
    }
  };
  const displayValue = parseFloat((value * 100).toFixed(decimalPlaces));

  return (
    <TextField
      {...textFieldProps}
      type="number"
      label={label}
      value={displayValue}
      onChange={handleChange}
      onWheel={() => {}}
      slotProps={{
        htmlInput: {
          min,
          max,
          step,
          ...textFieldProps.slotProps?.htmlInput,
        },
        input: {
          endAdornment: "%",
        },
      }}
      helperText={
        helperText || textFieldProps.disabled
          ? undefined
          : `Enter value as percentage (${min}-${max})`
      }
    />
  );
};

interface DisplayFieldProps {
  label: string;
  value: string;
  labelProps?: Omit<TypographyProps, "value">;
  valueProps?: Omit<TypographyProps, "value">;
}

export const DisplayField: React.FC<DisplayFieldProps> = ({
  label,
  value,
  labelProps,
  valueProps,
}) => {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      {labelProps ? (
        <Typography {...labelProps}>{label}</Typography>
      ) : (
        <Typography variant="body1">{label}</Typography>
      )}
      {valueProps ? (
        <Typography {...valueProps}>{value}</Typography>
      ) : (
        <Typography variant="h5">{value}</Typography>
      )}
    </Box>
  );
};
